import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore'
import { db } from '../firebase/config'
import { Image as ImageIcon, Loader, Filter, ChevronLeft, ChevronRight, Video, PlayCircle } from 'lucide-react'

const PAGE_SIZE = 12

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastDoc, setLastDoc] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedImage, setSelectedImage] = useState(null)
  const loadMoreRef = useRef(null)
  const scrollRestoreRef = useRef(null)

  // Spa~Radise Luxe 7 services + All & Other (aligned with admin gallery)
  const categories = ['All', 'Massage', 'Facials', 'Wax', 'Body Scrubbing', 'Manicures', 'Pedicures', 'Combo Packages', 'Other']

  const fetchPage = useCallback(async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      let q
      const base = collection(db, 'gallery')
      const order = orderBy('createdAt', 'desc')
      const limitQuery = limit(PAGE_SIZE)
      if (selectedCategory === 'All') {
        q = cursor
          ? query(base, where('isActive', '==', true), order, limitQuery, startAfter(cursor))
          : query(base, where('isActive', '==', true), order, limitQuery)
      } else {
        q = cursor
          ? query(base, where('isActive', '==', true), where('category', '==', selectedCategory), order, limitQuery, startAfter(cursor))
          : query(base, where('isActive', '==', true), where('category', '==', selectedCategory), order, limitQuery)
      }
      const snapshot = await getDocs(q)
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const newLast = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null
      setLastDoc(newLast)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) {
        setGalleryItems(prev => [...prev, ...items])
        return items
      } else {
        setGalleryItems(items)
      }
    } catch (error) {
      console.error('Error fetching gallery:', error)
      setHasMore(false)
      if (append) return []
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }, [selectedCategory])

  useEffect(() => {
    setLastDoc(null)
    setHasMore(true)
    fetchPage(null, false)
  }, [selectedCategory, fetchPage])

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return
    const el = loadMoreRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore && lastDoc) {
          const sentinelEl = loadMoreRef.current
          if (sentinelEl) {
            const rect = sentinelEl.getBoundingClientRect()
            scrollRestoreRef.current = {
              sentinelTopBefore: rect.top + window.scrollY,
              scrollYBefore: window.scrollY
            }
          }
          fetchPage(lastDoc, true)
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loading, loadingMore, lastDoc, fetchPage])

  // After loading more, keep scroll so the loader stays at the same viewport position (no jump to new items)
  useEffect(() => {
    if (loadingMore || !scrollRestoreRef.current) return
    const el = loadMoreRef.current
    const saved = scrollRestoreRef.current
    scrollRestoreRef.current = null
    if (!el) return
    const raf = requestAnimationFrame(() => {
      const sentinelTopAfter = el.getBoundingClientRect().top + window.scrollY
      const delta = sentinelTopAfter - saved.sentinelTopBefore
      if (delta !== 0) {
        window.scrollTo(window.scrollX, saved.scrollYBefore + delta)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [loadingMore])

  // Go to next item; load more from server if at last and hasMore
  const goToNext = useCallback(async () => {
    const currentIndex = galleryItems.findIndex(item => item.id === selectedImage?.id)
    if (currentIndex < 0) return
    if (currentIndex < galleryItems.length - 1) {
      setSelectedImage(galleryItems[currentIndex + 1])
      return
    }
    if (hasMore && !loadingMore && lastDoc) {
      const added = await fetchPage(lastDoc, true)
      if (added?.length) setSelectedImage(added[0])
    } else {
      setSelectedImage(galleryItems[0])
    }
  }, [galleryItems, selectedImage, hasMore, loadingMore, lastDoc, fetchPage])

  // Keyboard navigation for image modal
  useEffect(() => {
    if (!selectedImage || galleryItems.length === 0) return

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const currentIndex = galleryItems.findIndex(item => item.id === selectedImage.id)
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : galleryItems.length - 1
        setSelectedImage(galleryItems[prevIndex])
      } else if (e.key === 'ArrowRight') {
        goToNext()
      } else if (e.key === 'Escape') {
        setSelectedImage(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImage, galleryItems, goToNext])

  return (
    <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      {/* Hero Section - bodyscrub image background */}
      <div
        className="relative text-white py-16 md:py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/media/images/services/bodyscrub1.png)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 drop-shadow-lg">Gallery</h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto drop-shadow-md">
            Moments at Spa~Radise Luxe. Our treatments, spaces, and the calm we bring to you.
          </p>
        </div>
      </div>

      {/* Category Filter - dropdown */}
      <div className="container mx-auto px-4 py-8 pb-20">
        <div className="flex items-center justify-center mb-8">
          <div className="w-full max-w-xs">
            <label htmlFor="gallery-category" className="sr-only">Filter by category</label>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-500 pointer-events-none" />
              <select
                id="gallery-category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full appearance-none py-3 pl-11 pr-10 rounded-xl border-2 border-purple-200 bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400 cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23934eea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  backgroundSize: '1.25rem'
                }}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-12 h-12 animate-spin text-purple-500" />
          </div>
        ) : galleryItems.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-24 h-24 mx-auto text-purple-300 mb-4" />
            <p className="text-xl text-gray-600">No images found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedImage(item)}
                className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer transform transition-all hover:scale-105 hover:shadow-2xl"
              >
                <div className="aspect-square bg-gray-200">
                  {item.mediaType === 'video' ? (
                    <>
                      <video
                        src={item.imageUrl}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        preload="metadata"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                        <PlayCircle className="w-16 h-16 text-white opacity-80 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </>
                  ) : (
                    <img
                      src={item.imageUrl}
                      alt={item.title || 'Gallery image'}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x400?text=Image+Not+Found'
                      }}
                    />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                    <div className="flex items-center space-x-2 mb-1">
                      {item.mediaType === 'video' && (
                        <Video className="w-4 h-4" />
                      )}
                      <h3 className="font-semibold text-base md:text-lg">{item.title || 'Untitled'}</h3>
                    </div>
                    {item.description && (
                      <p className="text-xs md:text-sm text-gray-200 line-clamp-2">{item.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Sentinel for infinite scroll */}
        {!loading && galleryItems.length > 0 && (
          <div ref={loadMoreRef} className="flex justify-center py-8 min-h-[60px]">
            {loadingMore && (
              <Loader className="w-8 h-8 animate-spin text-purple-500" aria-hidden />
            )}
            {!hasMore && galleryItems.length > 0 && (
              <p className="text-sm text-gray-500">You've seen all gallery items</p>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              setSelectedImage(null)
            }
          }}
          style={{ zIndex: 99999 }}
        >
          <div className="max-w-4xl w-full relative" style={{ zIndex: 100000 }} onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Left Arrow */}
            {galleryItems.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  const currentIndex = galleryItems.findIndex(item => item.id === selectedImage.id)
                  const prevIndex = currentIndex > 0 ? currentIndex - 1 : galleryItems.length - 1
                  setSelectedImage(galleryItems[prevIndex])
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-3 hover:bg-black/70 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
              </button>
            )}

            {/* Right Arrow */}
            {galleryItems.length >= 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-3 hover:bg-black/70 transition-colors disabled:opacity-50"
                disabled={loadingMore && galleryItems.findIndex(item => item.id === selectedImage.id) === galleryItems.length - 1}
                aria-label="Next image"
              >
                {loadingMore && galleryItems.findIndex(item => item.id === selectedImage.id) === galleryItems.length - 1 ? (
                  <Loader className="w-6 h-6 md:w-8 md:h-8 animate-spin" />
                ) : (
                  <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                )}
              </button>
            )}

            {/* Media */}
            {selectedImage.mediaType === 'video' ? (
              <video
                src={selectedImage.imageUrl}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                controls
                autoPlay
              />
            ) : (
              <img
                src={selectedImage.imageUrl}
                alt={selectedImage.title || 'Gallery image'}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
            )}

            {/* Image Info */}
            <div className="mt-4 text-center text-white">
              <h3 className="text-xl md:text-2xl font-semibold mb-2">{selectedImage.title || 'Untitled'}</h3>
              {selectedImage.description && (
                <p className="text-sm md:text-base text-gray-300">{selectedImage.description}</p>
              )}
              {galleryItems.length > 1 && (
                <p className="text-xs md:text-sm text-gray-400 mt-2">
                  {galleryItems.findIndex(item => item.id === selectedImage.id) + 1} of {galleryItems.length}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Gallery
